/**
 * Update Hedge Config Dialog Component
 *
 * Allows users to modify hedge configuration for a running hedged strategy.
 * Updates leverage and rebalancing configuration without requiring strategy reload.
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Avatar,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import type { StrategyInfo } from "../../types/strategy";
import { traderTheme as theme } from "./traderDashboardTheme";
import { getExchangeLogoByName } from "../../utils/exchangeUrls";

/** Selected strategy info for bulk display */
export interface BulkStrategyDisplayInfo {
  strategyId: string;
  symbol: string;
  exchangeName: string;
  /** Current hedge config */
  hedgeConfig?: {
    leverage?: number;
    rebalancing?: {
      price_surge_threshold?: number;
      liquidation_proximity_threshold?: number;
      spot_futures_gap_magnitude_ratio?: number;
      entry_funding_rate_threshold?: number;
      low_funding_rate_threshold?: number;
      funding_time_window_seconds?: number;
    };
  };
}

export interface UpdateHedgeConfigDialogProps {
  open: boolean;
  strategy: StrategyInfo | null;
  onClose: () => void;
  onConfirm: (
    leverage?: number,
    rebalancing?: {
      price_surge_threshold?: number;
      liquidation_proximity_threshold?: number;
      spot_futures_gap_magnitude_ratio?: number;
      entry_funding_rate_threshold?: number;
      low_funding_rate_threshold?: number;
      funding_time_window_seconds?: number;
    },
  ) => void;
  loading: boolean;
  /** Optional custom title for bulk operations */
  title?: string;
  /** Selected strategies for bulk operations - displays exchange logos and symbols */
  selectedStrategies?: BulkStrategyDisplayInfo[];
}

const DEFAULT_SPOT_FUTURES_GAP_MAGNITUDE = -0.03;

/**
 * Get current hedge config from strategy execution context
 */
function getCurrentHedgeConfig(strategy: StrategyInfo | null) {
  if (!strategy?.execution_context?.hedge_config) {
    return {
      leverage: 2,
      rebalancing: {
        price_surge_threshold: 0.5,
        liquidation_proximity_threshold: 0.9,
        spot_futures_gap_magnitude_ratio: DEFAULT_SPOT_FUTURES_GAP_MAGNITUDE,
        low_funding_rate_threshold: -0.005,
        funding_time_window_seconds: 600,
      },
    };
  }
  return strategy.execution_context.hedge_config;
}

/**
 * Calculate average hedge config from selected strategies
 */
function calculateAverageHedgeConfig(
  selectedStrategies?: BulkStrategyDisplayInfo[],
) {
  if (!selectedStrategies || selectedStrategies.length === 0) {
    return {
      leverage: 2,
      rebalancing: {
        price_surge_threshold: 0.5,
        liquidation_proximity_threshold: 0.9,
        spot_futures_gap_magnitude_ratio: DEFAULT_SPOT_FUTURES_GAP_MAGNITUDE,
        low_funding_rate_threshold: -0.005,
        funding_time_window_seconds: 600,
      },
    };
  }

  const configs = selectedStrategies
    .filter((s) => s.hedgeConfig)
    .map((s) => s.hedgeConfig!);

  if (configs.length === 0) {
    return {
      leverage: 2,
      rebalancing: {
        price_surge_threshold: 0.5,
        liquidation_proximity_threshold: 0.9,
        spot_futures_gap_magnitude_ratio: DEFAULT_SPOT_FUTURES_GAP_MAGNITUDE,
        low_funding_rate_threshold: -0.005,
        funding_time_window_seconds: 600,
      },
    };
  }

  const avgLeverage = Math.round(
    configs.reduce((sum, c) => sum + (c.leverage || 2), 0) / configs.length,
  );

  const avgPriceSurge =
    configs.reduce(
      (sum, c) => sum + (c.rebalancing?.price_surge_threshold || 0.5),
      0,
    ) / configs.length;

  const avgLiquidationProximity =
    configs.reduce(
      (sum, c) => sum + (c.rebalancing?.liquidation_proximity_threshold || 0.9),
      0,
    ) / configs.length;

  const avgSpotFuturesGapMagnitude =
    configs.reduce(
      (sum, c) =>
        sum +
        (c.rebalancing?.spot_futures_gap_magnitude_ratio ??
          DEFAULT_SPOT_FUTURES_GAP_MAGNITUDE),
      0,
    ) / configs.length;

  const avgFundingRate =
    configs.reduce(
      (sum, c) => sum + (c.rebalancing?.low_funding_rate_threshold || -0.005),
      0,
    ) / configs.length;

  const avgTimeWindow = Math.round(
    configs.reduce(
      (sum, c) => sum + (c.rebalancing?.funding_time_window_seconds || 600),
      0,
    ) / configs.length,
  );

  return {
    leverage: avgLeverage,
    rebalancing: {
      price_surge_threshold: avgPriceSurge,
      liquidation_proximity_threshold: avgLiquidationProximity,
      spot_futures_gap_magnitude_ratio: avgSpotFuturesGapMagnitude,
      low_funding_rate_threshold: avgFundingRate,
      funding_time_window_seconds: avgTimeWindow,
    },
  };
}

export const UpdateHedgeConfigDialog: React.FC<
  UpdateHedgeConfigDialogProps
> = ({
  open,
  strategy,
  onClose,
  onConfirm,
  loading,
  title,
  selectedStrategies,
}) => {
  const { t } = useTranslation();
  const currentConfig = getCurrentHedgeConfig(strategy);

  // State for inputs
  const [leverage, setLeverage] = useState<string>("");
  const [priceSurgeThreshold, setPriceSurgeThreshold] = useState<string>("");
  const [liquidationProximity, setLiquidationProximity] = useState<string>("");
  const [spotFuturesGapMagnitude, setSpotFuturesGapMagnitude] =
    useState<string>("");
  const [lowFundingRate, setLowFundingRate] = useState<string>("");
  const [fundingTimeWindow, setFundingTimeWindow] = useState<string>("");

  const [error, setError] = useState<string>("");

  // Reset state when dialog opens (only on open transition, not while open)
  useEffect(() => {
    if (open) {
      // Use average values for bulk mode, single strategy config for single mode
      const config = strategy
        ? getCurrentHedgeConfig(strategy)
        : calculateAverageHedgeConfig(selectedStrategies);
      setLeverage(config.leverage?.toString() || "2");
      setPriceSurgeThreshold(
        ((config.rebalancing?.price_surge_threshold || 0.5) * 100).toString(),
      );
      setLiquidationProximity(
        (
          (config.rebalancing?.liquidation_proximity_threshold || 0.9) * 100
        ).toString(),
      );
      setSpotFuturesGapMagnitude(
        (
          (config.rebalancing?.spot_futures_gap_magnitude_ratio ??
            DEFAULT_SPOT_FUTURES_GAP_MAGNITUDE) * 100
        ).toString(),
      );
      setLowFundingRate(
        (
          (config.rebalancing?.low_funding_rate_threshold || -0.005) * 100
        ).toString(),
      );
      setFundingTimeWindow(
        (config.rebalancing?.funding_time_window_seconds || 600).toString(),
      );
      setError("");
    }
    // Only reset when dialog opens, not when selectedStrategies changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, strategy]);

  const handleConfirm = () => {
    setError("");

    // Parse values
    const leverageNum = leverage.trim() ? parseInt(leverage) : undefined;
    const priceSurgeNum = priceSurgeThreshold.trim()
      ? parseFloat(priceSurgeThreshold) / 100
      : undefined;
    const liquidationNum = liquidationProximity.trim()
      ? parseFloat(liquidationProximity) / 100
      : undefined;
    const spotFuturesGapNum = spotFuturesGapMagnitude.trim()
      ? parseFloat(spotFuturesGapMagnitude) / 100
      : undefined;
    const fundingRateNum = lowFundingRate.trim()
      ? parseFloat(lowFundingRate) / 100
      : undefined;
    const timeWindowNum = fundingTimeWindow.trim()
      ? parseInt(fundingTimeWindow)
      : undefined;

    // Validate leverage
    if (
      leverageNum !== undefined &&
      (isNaN(leverageNum) || leverageNum < 1 || leverageNum > 20)
    ) {
      setError(t("traderDashboard.updateHedgeConfig.errorInvalidLeverage"));
      return;
    }

    // Validate liquidation proximity
    if (
      liquidationNum !== undefined &&
      (isNaN(liquidationNum) || liquidationNum < 0.5 || liquidationNum >= 1)
    ) {
      setError(
        t("traderDashboard.updateHedgeConfig.errorInvalidLiquidationProximity"),
      );
      return;
    }

    if (
      spotFuturesGapNum !== undefined &&
      !Number.isFinite(spotFuturesGapNum)
    ) {
      setError(
        t(
          "traderDashboard.updateHedgeConfig.errorInvalidSpotFuturesGapMagnitude",
        ),
      );
      return;
    }

    // Validate funding rate
    if (
      fundingRateNum !== undefined &&
      (isNaN(fundingRateNum) || fundingRateNum > -0.001)
    ) {
      setError(t("traderDashboard.updateHedgeConfig.errorInvalidFundingRate"));
      return;
    }

    // Validate time window
    if (
      timeWindowNum !== undefined &&
      (isNaN(timeWindowNum) || timeWindowNum < 30 || timeWindowNum > 3600)
    ) {
      setError(t("traderDashboard.updateHedgeConfig.errorInvalidTimeWindow"));
      return;
    }

    // Build rebalancing config (only include fields that were modified)
    const rebalancing =
      priceSurgeNum !== undefined ||
      liquidationNum !== undefined ||
      spotFuturesGapNum !== undefined ||
      fundingRateNum !== undefined ||
      timeWindowNum !== undefined
        ? {
            price_surge_threshold: priceSurgeNum,
            liquidation_proximity_threshold: liquidationNum,
            spot_futures_gap_magnitude_ratio: spotFuturesGapNum,
            low_funding_rate_threshold: fundingRateNum,
            funding_time_window_seconds: timeWindowNum,
          }
        : undefined;

    onConfirm(leverageNum, rebalancing);
  };

  // Check if hedged strategy - only validate in single mode, skip in bulk mode
  const isHedgedStrategy =
    !strategy ||
    (strategy?.execution_context?.hedge_config !== null &&
      strategy?.execution_context?.hedge_config !== undefined);

  // Get symbol and exchange info
  const symbol =
    strategy?.execution_context?.opportunity?.base ||
    strategy?.execution_context?.position?.symbol?.replace("USDT", "") ||
    "-";
  const spotExchange = strategy?.execution_context?.opportunity?.buy_exchange;
  const spotExchangeName = spotExchange ? spotExchange.toLowerCase() : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.bg.primary,
          border: `1px solid ${theme.border}`,
          color: theme.text.primary,
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: theme.bg.primary,
          borderBottom: `1px solid ${theme.border}`,
          color: theme.accent,
        }}
      >
        <Stack direction="column" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {strategy && (
              <Avatar
                src={getExchangeLogoByName(spotExchangeName)}
                sx={{ width: 28, height: 28 }}
              />
            )}
            <WarningIcon sx={{ color: theme.accent }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: theme.accent }}
            >
              {title ||
                t("traderDashboard.updateHedgeConfig.title", { symbol })}
            </Typography>
          </Stack>
          {/* Bulk mode: show selected strategies with exchange logos, symbols, and hedge config */}
          {selectedStrategies && selectedStrategies.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                p: 1.5,
                backgroundColor: theme.bg.secondary,
                borderRadius: 1,
                maxHeight: 150,
                overflowY: "auto",
              }}
            >
              {selectedStrategies.map((s) => (
                <Box
                  key={s.strategyId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    backgroundColor: theme.bg.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.border}`,
                  }}
                >
                  <Avatar
                    src={getExchangeLogoByName(s.exchangeName)}
                    sx={{ width: 18, height: 18 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: theme.text.primary, fontWeight: 500 }}
                  >
                    {s.symbol}
                  </Typography>
                  {s.hedgeConfig && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "monospace",
                        ml: 0.5,
                      }}
                      component="span"
                    >
                      <Typography component="span" sx={{ color: theme.accent }}>
                        [{s.hedgeConfig.leverage || 2}x,{" "}
                      </Typography>
                      <Typography component="span" sx={{ color: theme.profit }}>
                        {(
                          (s.hedgeConfig.rebalancing?.price_surge_threshold ||
                            0.5) * 100
                        ).toFixed(0)}
                        %
                      </Typography>
                      <Typography component="span" sx={{ color: theme.accent }}>
                        ,{" "}
                      </Typography>
                      <Typography component="span" sx={{ color: theme.loss }}>
                        {(
                          (s.hedgeConfig.rebalancing
                            ?.liquidation_proximity_threshold || 0.9) * 100
                        ).toFixed(0)}
                        %
                      </Typography>
                      <Typography component="span" sx={{ color: theme.accent }}>
                        ,{" "}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{ color: theme.text.primary }}
                      >
                        {(
                          (s.hedgeConfig.rebalancing
                            ?.low_funding_rate_threshold || -0.005) * 100
                        ).toFixed(2)}
                        %@
                        {s.hedgeConfig.rebalancing
                          ?.funding_time_window_seconds || 600}
                        s
                      </Typography>
                      <Typography component="span" sx={{ color: theme.accent }}>
                        ]
                      </Typography>
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {!isHedgedStrategy && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("traderDashboard.updateHedgeConfig.errorNotHedged")}
          </Alert>
        )}

        <Typography variant="body2" sx={{ color: theme.text.secondary, mb: 3 }}>
          {t("traderDashboard.updateHedgeConfig.description")}
        </Typography>

        {/* Current Config Display - only show for single strategy mode */}
        {strategy && (
          <Box
            sx={{
              p: 2,
              backgroundColor: theme.bg.hover,
              borderRadius: 1,
              mb: 3,
              border: `1px solid ${theme.border}`,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: theme.text.secondary, mb: 1 }}
            >
              {t("traderDashboard.updateHedgeConfig.currentConfig")}
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2" sx={{ color: theme.text.secondary }}>
                {t("traderDashboard.updateHedgeConfig.leverage")}:{" "}
                <Typography
                  component="span"
                  sx={{ color: theme.accent, fontWeight: 600 }}
                >
                  {currentConfig.leverage}x
                </Typography>
              </Typography>
              <Typography variant="body2" sx={{ color: theme.text.secondary }}>
                {t("traderDashboard.updateHedgeConfig.priceSurgeThreshold")}:{" "}
                <Typography
                  component="span"
                  sx={{ color: theme.profit, fontWeight: 600 }}
                >
                  {(
                    (currentConfig.rebalancing?.price_surge_threshold || 0.5) *
                    100
                  ).toFixed(1)}
                  %
                </Typography>
              </Typography>
              <Typography variant="body2" sx={{ color: theme.text.secondary }}>
                {t("traderDashboard.updateHedgeConfig.liquidationProximity")}:{" "}
                <Typography
                  component="span"
                  sx={{ color: theme.loss, fontWeight: 600 }}
                >
                  {(
                    (currentConfig.rebalancing
                      ?.liquidation_proximity_threshold || 0.9) * 100
                  ).toFixed(1)}
                  %
                </Typography>
              </Typography>
              <Typography variant="body2" sx={{ color: theme.text.secondary }}>
                {t("traderDashboard.updateHedgeConfig.spotFuturesGapMagnitude")}
                :{" "}
                <Typography
                  component="span"
                  sx={{ color: theme.loss, fontWeight: 600 }}
                >
                  {(
                    (currentConfig.rebalancing
                      ?.spot_futures_gap_magnitude_ratio ??
                      DEFAULT_SPOT_FUTURES_GAP_MAGNITUDE) * 100
                  ).toFixed(1)}
                  %
                </Typography>
              </Typography>
              <Typography variant="body2" sx={{ color: theme.text.secondary }}>
                {t("traderDashboard.updateHedgeConfig.lowFundingRate")}:{" "}
                <Typography
                  component="span"
                  sx={{ color: theme.text.primary, fontWeight: 600 }}
                >
                  {(
                    (currentConfig.rebalancing?.low_funding_rate_threshold ||
                      -0.005) * 100
                  ).toFixed(2)}
                  %
                </Typography>
              </Typography>
              <Typography variant="body2" sx={{ color: theme.text.secondary }}>
                {t("traderDashboard.updateHedgeConfig.fundingTimeWindow")}:{" "}
                <Typography
                  component="span"
                  sx={{ color: theme.text.primary, fontWeight: 600 }}
                >
                  {currentConfig.rebalancing?.funding_time_window_seconds ||
                    600}
                  s
                </Typography>
              </Typography>
            </Stack>
          </Box>
        )}

        <Divider sx={{ borderColor: theme.border, mb: 3 }} />

        {/* Leverage Input */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            type="number"
            label={t("traderDashboard.updateHedgeConfig.leverage")}
            value={leverage}
            onChange={(e) => setLeverage(e.target.value)}
            disabled={loading || !isHedgedStrategy}
            helperText={t("traderDashboard.updateHedgeConfig.leverageDesc")}
            InputProps={{
              inputProps: { min: 1, max: 20, step: 1 },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.text.primary,
                "& fieldset": { borderColor: theme.border },
                "&:hover fieldset": { borderColor: theme.accent },
              },
              "& .MuiInputLabel-root": { color: theme.text.secondary },
              "& .MuiFormHelperText-root": { color: theme.text.secondary },
            }}
          />
        </Box>

        {/* Rebalancing Config */}
        <Typography
          variant="subtitle2"
          sx={{ color: theme.text.secondary, mb: 2 }}
        >
          {t("traderDashboard.updateHedgeConfig.rebalancingConfig")}
        </Typography>

        <Stack spacing={2.5}>
          <TextField
            fullWidth
            type="number"
            label={t("traderDashboard.updateHedgeConfig.priceSurgeThreshold")}
            value={priceSurgeThreshold}
            onChange={(e) => setPriceSurgeThreshold(e.target.value)}
            disabled={loading || !isHedgedStrategy}
            helperText={t("traderDashboard.updateHedgeConfig.priceSurgeDesc")}
            InputProps={{
              inputProps: { min: 0, max: 200, step: 1 },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.text.primary,
                "& fieldset": { borderColor: theme.border },
                "&:hover fieldset": { borderColor: theme.accent },
              },
              "& .MuiInputLabel-root": { color: theme.text.secondary },
              "& .MuiFormHelperText-root": { color: theme.text.secondary },
            }}
          />

          <TextField
            fullWidth
            type="number"
            label={t("traderDashboard.updateHedgeConfig.liquidationProximity")}
            value={liquidationProximity}
            onChange={(e) => setLiquidationProximity(e.target.value)}
            disabled={loading || !isHedgedStrategy}
            helperText={t(
              "traderDashboard.updateHedgeConfig.liquidationProximityDesc",
            )}
            InputProps={{
              inputProps: { min: 50, max: 99, step: 1 },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.text.primary,
                "& fieldset": { borderColor: theme.border },
                "&:hover fieldset": { borderColor: theme.accent },
              },
              "& .MuiInputLabel-root": { color: theme.text.secondary },
              "& .MuiFormHelperText-root": { color: theme.text.secondary },
            }}
          />

          <TextField
            fullWidth
            type="text"
            label={t(
              "traderDashboard.updateHedgeConfig.spotFuturesGapMagnitude",
            )}
            value={spotFuturesGapMagnitude}
            onChange={(e) => setSpotFuturesGapMagnitude(e.target.value)}
            disabled={loading || !isHedgedStrategy}
            helperText={t(
              "traderDashboard.updateHedgeConfig.spotFuturesGapMagnitudeDesc",
            )}
            InputProps={{
              inputProps: { inputMode: "decimal" },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.text.primary,
                "& fieldset": { borderColor: theme.border },
                "&:hover fieldset": { borderColor: theme.accent },
              },
              "& .MuiInputLabel-root": { color: theme.text.secondary },
              "& .MuiFormHelperText-root": { color: theme.text.secondary },
            }}
          />

          <TextField
            fullWidth
            type="number"
            label={t("traderDashboard.updateHedgeConfig.lowFundingRate")}
            value={lowFundingRate}
            onChange={(e) => setLowFundingRate(e.target.value)}
            disabled={loading || !isHedgedStrategy}
            helperText={t(
              "traderDashboard.updateHedgeConfig.lowFundingRateDesc",
            )}
            InputProps={{
              inputProps: { min: -10, max: -0.1, step: 0.01 },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.text.primary,
                "& fieldset": { borderColor: theme.border },
                "&:hover fieldset": { borderColor: theme.accent },
              },
              "& .MuiInputLabel-root": { color: theme.text.secondary },
              "& .MuiFormHelperText-root": { color: theme.text.secondary },
            }}
          />

          <TextField
            fullWidth
            type="number"
            label={t("traderDashboard.updateHedgeConfig.fundingTimeWindow")}
            value={fundingTimeWindow}
            onChange={(e) => setFundingTimeWindow(e.target.value)}
            disabled={loading || !isHedgedStrategy}
            helperText={t(
              "traderDashboard.updateHedgeConfig.fundingTimeWindowDesc",
            )}
            InputProps={{
              inputProps: { min: 30, max: 3600, step: 30 },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: theme.text.primary,
                "& fieldset": { borderColor: theme.border },
                "&:hover fieldset": { borderColor: theme.accent },
              },
              "& .MuiInputLabel-root": { color: theme.text.secondary },
              "& .MuiFormHelperText-root": { color: theme.text.secondary },
            }}
          />
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${theme.border}`,
          px: 3,
          py: 2,
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            color: theme.text.secondary,
            "&:hover": {
              backgroundColor: theme.bg.hover,
            },
          }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading || !isHedgedStrategy}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{
            backgroundColor: theme.accent,
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: theme.accentHover,
            },
            "&.Mui-disabled": {
              backgroundColor: theme.bg.hover,
              color: theme.text.secondary,
            },
          }}
        >
          {loading
            ? t("traderDashboard.updateHedgeConfig.updating")
            : t("traderDashboard.updateHedgeConfig.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateHedgeConfigDialog;
